/**
 * Multi-resolution data loading helpers
 * 
 * These helpers support progressive data loading for track charts,
 * similar to the deep zoom pattern used for images.
 */

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface Scale {
  scale: number;          // 1 = raw/highest resolution, 2 = half, 4 = quarter, 8 = eighth, etc. (powers of 2)
  pointCount: number;     // Total number of data points at this scale
  depthInterval: number;  // Spacing between points (e.g., 0.01m, 0.02m, 0.04m)
}

export interface ScaleMetadata {
  boreholeId?: number;     // Optional: included when fetching multi-borehole metadata
  trackTypeId: number;
  totalLevels: number;
  scales: Scale[];
  minimumDepth?: number;   // Full depth extent of this track type for this borehole
  maximumDepth?: number;
}

export interface TrackDataCacheEntry {
  trackTypeId: number;
  boreholeId?: number;
  scale: number;          // Scale used for this cached data (1, 2, 4, 8, 16...)
  startDepth: number;
  endDepth: number;
  data: any[];
  timestamp: number;      // When this data was cached
}

export interface TrackDataCache {
  [key: string]: TrackDataCacheEntry;  // Key format: "trackTypeId:boreholeId:scale"
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate cache key for storing track data
 */
export function getCacheKey(trackTypeId: number, boreholeId: number | undefined, scale: number): string {
  return `${trackTypeId}:${boreholeId ?? 'single'}:${scale}`;
}

/**
 * Calculate the optimal scale to fetch based on visible depth range
 * 
 * Strategy: Select the scale that will give approximately 2000-3000 points
 * in the visible range. This provides good visual detail without overwhelming
 * the rendering performance.
 * 
 * Scale values are powers of 2: 1 (raw), 2 (half), 4 (quarter), 8 (eighth), etc.
 * Lower scale = higher resolution (more points)
 * 
 * @param depthRange - The depth range currently visible (endDepth - startDepth)
 * @param resolutions - Available resolution levels for this track type
 * @param targetPointCount - Target number of points to display (default: 2000)
 * @returns The optimal scale to fetch (1, 2, 4, 8, 16, etc.)
 */
export function selectOptimalResolution(
  depthRange: number,
  resolutions: Scale[],
  targetPointCount: number = 2000
): number {
  if (!resolutions || resolutions.length === 0) {
    return 1; // Default to raw data (scale 1) if metadata not available
  }

  // Sort resolutions by scale (1 = highest resolution, larger = lower resolution)
  const sortedResolutions = [...resolutions].sort((a, b) => a.scale - b.scale);

  // Calculate how many points each scale would give us in this depth range
  const resolutionScores = sortedResolutions.map(res => {
    const estimatedPoints = depthRange / res.depthInterval;
    const score = Math.abs(estimatedPoints - targetPointCount);
    return { scale: res.scale, estimatedPoints, score };
  });

  // Find the scale with the closest point count to our target
  resolutionScores.sort((a, b) => a.score - b.score);
  
  // If the best match would give us too few points (< 50% of target),
  // prefer a higher resolution (lower scale number)
  const bestMatch = resolutionScores[0];
  if (bestMatch.estimatedPoints < targetPointCount * 0.5 && bestMatch.scale > 1) {
    // Try to use a higher resolution (lower scale)
    const higherResolution = resolutionScores.find(
      r => r.scale < bestMatch.scale && r.estimatedPoints >= targetPointCount * 0.5
    );
    if (higherResolution) {
      return higherResolution.scale;
    }
  }

  return bestMatch.scale;
}

/**
 * Check if we have adequate data coverage in the cache for the requested range
 * 
 * Returns true if:
 * 1. We have data at the exact scale
 * 2. The cached data fully covers the requested depth range
 * 3. The cached data is reasonably fresh (< 5 minutes old)
 * 
 * @param cache - The track data cache
 * @param trackTypeId - Track type to check
 * @param boreholeId - Borehole ID (optional for multi-borehole views)
 * @param scale - Scale needed (1, 2, 4, 8, etc.)
 * @param startDepth - Start of depth range
 * @param endDepth - End of depth range
 * @param maxAgeMs - Maximum age of cached data in milliseconds (default: 5 minutes)
 * @returns True if cache has adequate coverage
 */
export function hasAdequateCacheCoverage(
  cache: TrackDataCache,
  trackTypeId: number,
  boreholeId: number | undefined,
  scale: number,
  startDepth: number,
  endDepth: number,
  maxAgeMs: number = 5 * 60 * 1000
): boolean {
  const cacheKey = getCacheKey(trackTypeId, boreholeId, scale);
  const cached = cache && cacheKey && cache[cacheKey];

  if (!cached) {
    return false;
  }

  // Check if data is too old
  const now = Date.now();
  if (now - cached.timestamp > maxAgeMs) {
    return false;
  }

  // Check if cached range covers requested range
  // We allow a small tolerance for floating point comparison
  const tolerance = 0.001;
  const coverageStart = cached.startDepth - tolerance;
  const coverageEnd = cached.endDepth + tolerance;

  return coverageStart <= startDepth && coverageEnd >= endDepth;
}

/**
 * Calculate depth range with buffer for prefetching
 * 
 * When fetching data, we fetch a bit more than the visible range
 * to reduce the need for additional fetches when the user scrolls slightly.
 * 
 * @param startDepth - Visible start depth
 * @param endDepth - Visible end depth
 * @param bufferPercentage - Percentage of range to add as buffer (default: 50%)
 * @returns Buffered depth range
 */
export function getBufferedDepthRange(
  startDepth: number,
  endDepth: number,
  bufferPercentage: number = 50
): { bufferedStart: number; bufferedEnd: number } {
  const depthRange = endDepth - startDepth;
  const buffer = depthRange * (bufferPercentage / 100);

  return {
    bufferedStart: Math.max(0, startDepth - buffer),
    bufferedEnd: endDepth + buffer
  };
}

/**
 * Extract data from cache for a specific depth range
 * 
 * Filters cached data to only return points within the requested range.
 * This is useful when we have a larger cached range and only need a subset.
 * 
 * @param cached - Cached track data entry
 * @param startDepth - Start of requested range
 * @param endDepth - End of requested range
 * @returns Filtered data array
 */
export function extractDataFromCache(
  cached: TrackDataCacheEntry,
  startDepth: number,
  endDepth: number
): any[] {
  if (!cached || !cached.data) {
    return [];
  }

  // Filter data points within the requested depth range
  return cached.data.filter((point: any) => {
    const depth = point.depth ?? point.fromDepth ?? point.y;
    return depth >= startDepth && depth <= endDepth;
  });
}

/**
 * Determine if a higher resolution should be fetched
 * 
 * When the user zooms in significantly, we may want to fetch higher
 * resolution data to show more detail.
 * 
 * @param currentScale - Currently displayed scale
 * @param optimalScale - Optimal scale for current zoom level
 * @returns True if we should fetch higher resolution
 */
export function shouldFetchHigherResolution(
  currentScale: number,
  optimalScale: number
): boolean {
  // Fetch higher resolution if optimal scale is at least half the current
  // (remember: lower scale = higher resolution, so optimal < current)
  return optimalScale < currentScale / 2;
}

/**
 * Clean old entries from cache
 * 
 * Remove cached data that is older than the specified age.
 * This prevents the cache from growing indefinitely.
 * 
 * @param cache - Track data cache to clean
 * @param maxAgeMs - Maximum age in milliseconds (default: 10 minutes)
 * @returns Cleaned cache
 */
export function cleanCache(
  cache: TrackDataCache,
  maxAgeMs: number = 10 * 60 * 1000
): TrackDataCache {
  const now = Date.now();
  const cleaned: TrackDataCache = {};

  Object.entries(cache).forEach(([key, entry]) => {
    if (now - entry.timestamp <= maxAgeMs) {
      cleaned[key] = entry;
    }
  });

  return cleaned;
}

/**
 * Compute optimal scales for multiple track types
 *
 * Returns an array mapping each trackTypeId to its optimal scale
 * given the current visible depth range and available metadata.
 */
export function computeOptimalResolutionsForTrackTypes(
  trackTypeIds: number[],
  scaleMetadatas: { [trackTypeId: number]: ScaleMetadata } | undefined,
  depthRange: number,
  targetPointCount: number = 2000,
  // Optional: groups of trackTypeIds that must share the same scale (e.g., cumulative tracks with multiple curves)
  cumulativeGroups?: number[][]
): Array<{ trackTypeId: number; scale: number }> {  
  const entries = trackTypeIds.map((trackTypeId) => {
    const scaleMetadata = scaleMetadatas ? scaleMetadatas[trackTypeId] : undefined;
    const resolutions = scaleMetadata ? scaleMetadata?.scales ?? [] : [];
    const scale = selectOptimalResolution(depthRange, resolutions, targetPointCount);
    return { trackTypeId, scale };
  });
  
  // If cumulative groups are provided, enforce the same scale across each group.
  // Use the "lowest common denominator" (i.e., the smallest scale value -> highest resolution)
  // so that all curves in the cumulative track render correctly.
  if (Array.isArray(cumulativeGroups) && cumulativeGroups.length > 0) {
    cumulativeGroups.forEach(group => {
      const groupEntries = entries.filter(e => group.includes(e.trackTypeId));
      if (groupEntries.length > 0) {
        const minScale = Math.min(...groupEntries.map(g => g.scale));
        groupEntries.forEach(e => { e.scale = minScale; });
      }
    });
  }

  return entries;
}

export function buildCumulativeGroupsFromTracks(tracks: any[] | undefined): number[][] {
  if (!Array.isArray(tracks) || tracks.length === 0) return [];
  const groups: number[][] = [];
  const seen = new Set<string>();

  tracks.forEach((track: any) => {
    if (track?.trackType !== 3 || !Array.isArray(track?.curves)) return;
    const ids: number[] = track.curves
      .map((curve: any) => Number(curve?.trackTypeId))
      .filter((id: number) => !isNaN(id) && id !== 0);
    const uniqueIds: number[] = Array.from(new Set<number>(ids));
    if (uniqueIds.length < 2) return;
    uniqueIds.sort((a, b) => a - b);
    const key = uniqueIds.join(',');
    if (!seen.has(key)) {
      seen.add(key);
      groups.push(uniqueIds);
    }
  });

  return groups;
}

/**
 * Get the best available cached data for a track type within a depth range
 * 
 * This function looks for cached data in order of preference:
 * 1. Exact scale that covers the range
 * 2. Lower scale (higher resolution) that covers the range
 * 3. Higher scale (lower resolution) that covers the range - fallback
 * 4. Any cached data for this track type (partial coverage or any scale)
 * 
 * This ensures data is shown immediately during panning, even if it's
 * lower resolution or only partial coverage, until higher resolution data is fetched.
 * 
 * Note: The initially loaded data may be at any scale (not necessarily scale=1),
 * depending on the initial zoom level. This function will find whatever was
 * previously cached and use it as a fallback.
 * 
 * @param cache - The track data cache
 * @param trackTypeId - Track type to get data for
 * @param boreholeId - Borehole ID (optional)
 * @param preferredScale - Preferred scale to use (hint, not requirement)
 * @param startDepth - Start of depth range
 * @param endDepth - End of depth range
 * @param maxAgeMs - Maximum age of cached data (default: 5 minutes)
 * @returns Best available cached entry, or undefined if none found
 */
export function getBestAvailableCachedData(
  cache: TrackDataCache,
  trackTypeId: number,
  boreholeId: number | undefined,
  preferredScale: number,
  startDepth: number,
  endDepth: number,
  maxAgeMs: number = 5 * 60 * 1000
): TrackDataCacheEntry | undefined {
  if (!cache) return undefined;

  const now = Date.now();
  const tolerance = 0.001;

  // Helper to check if an entry covers the range and is fresh
  const isValidEntry = (entry: TrackDataCacheEntry, requireFullCoverage: boolean = true): boolean => {
    if (!entry) return false;
    if (now - entry.timestamp > maxAgeMs) return false;
    
    if (requireFullCoverage) {
      const coverageStart = entry.startDepth - tolerance;
      const coverageEnd = entry.endDepth + tolerance;
      return coverageStart <= startDepth && coverageEnd >= endDepth;
    }
    
    // For partial coverage, just check if there's any overlap
    return entry.endDepth >= startDepth && entry.startDepth <= endDepth;
  };

  // Try exact scale first with full coverage
  const exactKey = getCacheKey(trackTypeId, boreholeId, preferredScale);
  if (cache[exactKey] && isValidEntry(cache[exactKey], true)) {
    return cache[exactKey];
  }

  // Collect all valid cached entries for this track type with full coverage
  const fullCoverageEntries: TrackDataCacheEntry[] = [];
  const partialCoverageEntries: TrackDataCacheEntry[] = [];
  
  Object.keys(cache).forEach(key => {
    const entry = cache[key];
    if (entry.trackTypeId === trackTypeId &&
        (boreholeId === undefined || entry.boreholeId === boreholeId)) {
      if (isValidEntry(entry, true)) {
        fullCoverageEntries.push(entry);
      } else if (isValidEntry(entry, false)) {
        partialCoverageEntries.push(entry);
      }
    }
  });

  // Prefer full coverage entries
  if (fullCoverageEntries.length > 0) {
    // Sort by scale: prefer higher resolution (lower scale), but accept lower resolution (higher scale) as fallback
    fullCoverageEntries.sort((a, b) => {
      // If both are higher res than preferred, pick the closest
      if (a.scale <= preferredScale && b.scale <= preferredScale) {
        return Math.abs(a.scale - preferredScale) - Math.abs(b.scale - preferredScale);
      }
      // If one is higher res and one is lower res, prefer higher res
      if (a.scale <= preferredScale) return -1;
      if (b.scale <= preferredScale) return 1;
      // Both are lower res, pick the closest (smallest scale difference)
      return a.scale - b.scale;
    });
    
    return fullCoverageEntries[0];
  }

  // Fall back to partial coverage - better to show something than nothing
  if (partialCoverageEntries.length > 0) {
    // Sort by amount of overlap and scale
    partialCoverageEntries.sort((a, b) => {
      const overlapA = Math.min(a.endDepth, endDepth) - Math.max(a.startDepth, startDepth);
      const overlapB = Math.min(b.endDepth, endDepth) - Math.max(b.startDepth, startDepth);
      
      // First prefer more overlap
      if (Math.abs(overlapA - overlapB) > 0.1) {
        return overlapB - overlapA;
      }
      
      // Then prefer better scale
      if (a.scale <= preferredScale && b.scale <= preferredScale) {
        return Math.abs(a.scale - preferredScale) - Math.abs(b.scale - preferredScale);
      }
      if (a.scale <= preferredScale) return -1;
      if (b.scale <= preferredScale) return 1;
      return a.scale - b.scale;
    });
    
    return partialCoverageEntries[0];
  }

  return undefined;
}
